/* eslint-env jest */
import {
  LAYOUTS, BG_SUBSTRINGS, selectLayout, BUY_ALL_TILE_LAYOUT, BUY_ALL_BLANK_LAYOUT,
  namedLayoutFor,
} from './packageLayouts';
import fixture from './packageLayouts.fixture.json';

// packageLayouts.fixture.json is a snapshot of every real package option in the
// database ([packageId, optionName, expectedLayoutKey|null]) captured from the
// ORIGINAL AbsolutePosImgs component. It is the contract that the data-driven
// replacement reproduces the legacy preview choice for every option. If you
// intentionally change a layout's selection, regenerate the fixture in the same
// commit.

describe('selectLayout — parity with the legacy component', () => {
  it('reproduces the legacy layout choice for every real option (all 2,322)', () => {
    const mismatches = fixture
      .filter(([packageId, optionName, expected]) => selectLayout(packageId, optionName) !== expected)
      .map(([packageId, optionName, expected]) => ({
        packageId, optionName, expected, got: selectLayout(packageId, optionName),
      }));
    expect(mismatches).toEqual([]);
  });

  it('never returns a key that is missing from LAYOUTS', () => {
    const badKeys = fixture
      .map(([packageId, optionName]) => selectLayout(packageId, optionName))
      .filter((key) => key !== null && !LAYOUTS[key]);
    expect(badKeys).toEqual([]);
  });

  it('treats optionName matching as substring (loose), like the legacy code', () => {
    expect(selectLayout(629, 'Star Student Package 2027')).toBe('p629StarStudent');
    expect(selectLayout(611, 'Picture Perfect Package (Fall)')).toBe('picturePerfect');
  });

  it('accepts a string packageId (legacy used loose ==)', () => {
    expect(selectLayout('629', 'Star Student Package')).toBe('p629StarStudent');
    expect(selectLayout('610', 'Double Double')).toBe('p610DoubleDouble');
    expect(selectLayout('549', 'All Star')).toBe('starStudent');
  });

  it('scopes Star/Memories to their packages and routes 629 to its own layouts', () => {
    expect(selectLayout(549, 'All Star')).toBe('starStudent');
    expect(selectLayout(611, 'Star Student Package')).toBe('starStudent');
    expect(selectLayout(629, 'Star Student Package')).toBe('p629StarStudent'); // 629 override
    expect(selectLayout(999, 'All Star')).toBeNull(); // not a Star package
    expect(selectLayout(629, 'Memories Package')).toBe('p629Memories'); // 629 override
    expect(selectLayout(611, 'Memories Package')).toBe('memories');
  });

  it('routes the 2026-27 fall "Star Student" top tier to its own layout, scoped to 877/778', () => {
    expect(selectLayout(877, 'Star Student')).toBe('starStudent2027'); // EL Fall 2027
    expect(selectLayout(778, 'Star Student')).toBe('starStudent2027'); // JH-HS Fall 2027
    expect(selectLayout('877', 'Star Student Collection')).toBe('starStudent2027');
    expect(selectLayout(584, 'Star Student Package')).toBeNull(); // retired package keeps no preview
    expect(selectLayout(611, 'Star Student Package')).toBe('starStudent'); // older layout untouched
    expect(LAYOUTS.starStudent2027.filter(Boolean)).toHaveLength(13); // 8x10, 2x 5x7, 3x5, 8 wallets, phone
  });

  it('routes 976 "Superstar" to the sports layout, scoped to that package', () => {
    expect(selectLayout(976, 'Superstar')).toBe('sportsSuperstar');
    expect(selectLayout('976', 'Superstar')).toBe('sportsSuperstar'); // id may arrive as a string
    // "Superstar" is reused across sports pricelists whose art is DIFFERENT, and
    // this geometry was measured against 976's banner alone.
    expect(selectLayout(649, 'Superstar')).toBeNull();
    expect(selectLayout(656, 'Superstar')).toBeNull();
    expect(selectLayout(628, 'Superstar')).toBeNull();
    // 549's Star rule is case-sensitive on 'Star', so it never claimed
    // "Superstar" — only its "All Star" option. Unchanged by this layout.
    expect(selectLayout(549, 'Superstar')).toBeNull();
    expect(selectLayout(549, 'All Star')).toBe('starStudent');
    // 8x10, 5x7, 3x5, 8 wallets, team, phone
    expect(LAYOUTS.sportsSuperstar.filter(Boolean)).toHaveLength(13);
  });

  it('gives the sports team slot the landscape role and the phone its own aspect', () => {
    const slots = LAYOUTS.sportsSuperstar.filter(Boolean);
    const horiz = slots.filter((slot) => slot.pose === 'poseHoriz');
    expect(horiz).toHaveLength(1); // the team picture, the one landscape frame

    // ⚠️ NOTHING in this layout may carry a frame. frame: 'phone' paints
    // PHONE_FRAME_STYLE's #1c1c1e over the slot, and this art draws its own
    // bezel — a transparent cutout then stands on a black rectangle instead of
    // the artwork's screen. Caught in Chrome; this keeps it caught.
    expect(slots.every((slot) => !slot.frame)).toBe(true);

    // The phone screen slot is the PHOTO area inside the art's own bezel --
    // below the SHOOB logo header, above the home indicator -- so it is nothing
    // like 4:5 and pins its own aspect. Re-measured on the 2026-10-07 blank art
    // (the previous art had no logo header, and the slot was the whole 128x274
    // screen).
    const phone = slots.find((slot) => slot.aspectRatio);
    expect(phone.aspectRatio).toBe('122 / 151');
  });

  it('returns null for options with no preview layout', () => {
    expect(selectLayout(611, 'Magnets')).toBeNull();
    expect(selectLayout(611, 'Buy All Your Photos')).toBeNull();
    expect(selectLayout(123, 'Some Unknown Package')).toBeNull();
  });
});

describe('LAYOUTS — structural integrity', () => {
  const POSE_ROLES = new Set([
    'pose1', 'pose2', 'pose3', 'pose4', 'poseHoriz', 'springpose1', 'springpose2',
  ]);

  it('has exactly the 34 known layouts', () => {
    expect(Object.keys(LAYOUTS)).toHaveLength(34);
  });

  it('every layout is an array of 16 slots', () => {
    Object.values(LAYOUTS).forEach((slots) => {
      expect(Array.isArray(slots)).toBe(true);
      expect(slots).toHaveLength(16);
    });
  });

  it('every visible slot has valid geometry, a known pose role, and a known/blank background', () => {
    Object.entries(LAYOUTS).forEach(([key, slots]) => {
      slots.forEach((slot, i) => {
        if (slot === null) return; // hidden slot
        const where = `${key}[${i}]`;
        expect(slot.top).toMatch(/%$/);
        expect(slot.left).toMatch(/%$/);
        expect(slot.width).toMatch(/%$/);
        expect(POSE_ROLES.has(slot.pose)).toBe(true);
        if (slot.background !== null) {
          expect(BG_SUBSTRINGS[slot.background]).toBeDefined();
        }
        expect(where).toBeTruthy();
      });
    });
  });

  it('every layout has at least one visible slot', () => {
    Object.values(LAYOUTS).forEach((slots) => {
      expect(slots.some((s) => s !== null)).toBe(true);
    });
  });
});

describe('namedLayoutFor - named layouts resolve by key', () => {
  const named = { buyAllTile: BUY_ALL_TILE_LAYOUT, buyAllBlank: BUY_ALL_BLANK_LAYOUT };
  it('returns the named layout, and null for unknown keys', () => {
    // buyAllTile keeps the two-print geometry the LIVE seniors banner is
    // drawn for; the blank series carries two prints + five phone tiles.
    expect(namedLayoutFor('buyAllTile', named)).toHaveLength(2);
    expect(namedLayoutFor('buyAllBlank', named)).toHaveLength(7);
    expect(BUY_ALL_BLANK_LAYOUT.every((s) => s.frame === null && s.background === null)).toBe(true);
    expect(namedLayoutFor('nope', named)).toBeNull();
  });
});

// ---------------------------------------------------------------------------
// SPORTS — package 976 "Fall Sports 2026-2027v1".
//
// Seven options ship art with empty print placeholders, each measured against
// its OWN file. These pin the routing (every rule is package-scoped) and the
// shape of each layout. See the block comment above LAYOUTS.sportsSuperstar.
describe('selectLayout — package 976 sports options', () => {
  // The seven sellable options whose art ships blank, as they exist on
  // production (verified against the options table 2026-10-07), and the layout
  // each must route to.
  const SPORTS_OPTIONS = [
    ['Superstar', 'sportsSuperstar', 13],
    ['All Star', 'sportsAllStar', 14],
    ['Picture Perfect Sports', 'sportsPicturePerfect', 13],
    ['Team Picture Only', 'sportsTeamPicture', 1],
    ['The Digital Package', 'sportsAllDigital', 1],
    ['Only an 8x10', 'sports8x10', 1],
    ['Basic', 'sportsBasic', 11],
  ];

  it.each(SPORTS_OPTIONS)('routes 976 %s to %s with %i slots', (name, key, slots) => {
    expect(selectLayout(976, name)).toBe(key);
    expect(selectLayout('976', name)).toBe(key); // id may arrive as a string
    expect(LAYOUTS[key].filter(Boolean)).toHaveLength(slots);
  });

  // The whole point of scoping to 976: these names are reused across sports
  // pricelists whose art is DIFFERENT, and this geometry was measured against
  // 976's files alone.
  it.each(SPORTS_OPTIONS)('does NOT claim %s on another sports pricelist', (name) => {
    [628, 580, 622, 649, 656, 664].forEach((pkg) => {
      expect(selectLayout(pkg, name)).not.toBe(selectLayout(976, name));
    });
  });

  // selectLayout is NOT art-gated -- the package builder draws an option preview
  // from it alone -- so a rule must never match an option whose art is
  // self-contained. These three on 976 keep their own finished art.
  it('leaves 976 options with self-contained art bare', () => {
    expect(selectLayout(976, 'Digital Value Pack')).toBeNull();
    expect(selectLayout(976, 'Just A Digital')).toBeNull();
    expect(selectLayout(976, 'Youth Sports Options')).toBeNull();
  });

  it('uses only the two individual poses and the team picture', () => {
    // A sports student is photographed twice plus a team shot, so these layouts
    // draw -01, -02 and the landscape team frame -- never pose3/pose4.
    SPORTS_OPTIONS.forEach(([, key]) => {
      LAYOUTS[key].filter(Boolean).forEach((slot) => {
        expect(['pose1', 'pose2', 'poseHoriz']).toContain(slot.pose);
      });
    });
  });

  it('gives every sports layout exactly one landscape team slot, or none', () => {
    SPORTS_OPTIONS.forEach(([, key]) => {
      const horiz = LAYOUTS[key].filter(Boolean).filter((s) => s.pose === 'poseHoriz');
      expect(horiz.length).toBeLessThanOrEqual(1);
    });
    // The three that advertise "Includes Team Picture!" must actually have one.
    ['sportsSuperstar', 'sportsAllStar', 'sportsPicturePerfect', 'sportsTeamPicture']
      .forEach((key) => {
        expect(LAYOUTS[key].filter(Boolean).filter((s) => s.pose === 'poseHoriz')).toHaveLength(1);
      });
  });

  // ⚠️ frame: 'phone' paints PHONE_FRAME_STYLE's #1c1c1e over the slot. Every
  // one of these arts draws its own bezel and the slot is only the photo area
  // inside the screen, so a painted frame stands the student on a black
  // rectangle. Caught in Chrome once; this keeps it caught.
  it('never paints a phone frame over art that draws its own', () => {
    SPORTS_OPTIONS.forEach(([, key]) => {
      expect(LAYOUTS[key].filter(Boolean).every((slot) => !slot.frame)).toBe(true);
    });
  });

  // Each print measured 4:5 and each team shot 5:4, so only the phone -- the
  // photo area inside the art's own bezel -- pins an aspect ratio.
  it('pins an aspect ratio only on the phone slots', () => {
    SPORTS_OPTIONS.forEach(([, key]) => {
      const pinned = LAYOUTS[key].filter(Boolean).filter((s) => s.aspectRatio);
      expect(pinned.length).toBeLessThanOrEqual(1);
    });
    expect(LAYOUTS.sportsAllDigital.filter(Boolean)[0].aspectRatio).toBe('200 / 250');
  });

  // The art supplies the backdrop inside every print, so no slot asks the
  // compositor to paint one. A `background:` tag here would be matched against
  // the SCHOOL's background set, which a sports school may not have at all.
  it('never asks the compositor for a background', () => {
    SPORTS_OPTIONS.forEach(([, key]) => {
      LAYOUTS[key].filter(Boolean).forEach((slot) => expect(slot.background).toBeNull());
    });
  });
});

// ---------------------------------------------------------------------------
// One sheet, one pose.
//
// A sheet is a single piece of paper the lab prints and cuts. Every print
// inside one white frame on the artwork is therefore the SAME photograph, and
// two poses on one sheet is not something that can be produced. The dividers
// are thin -- the 5x7 pairs read as two sheets until you zoom in -- so this
// pins the groupings rather than leaving them to the eye.
describe('LAYOUTS — prints that share a sheet share a pose', () => {
  // slot indices that sit inside ONE white frame on each art, verified by
  // zooming the blank files: an outer frame with a hairline divider.
  const SHEETS = {
    // the eight wallets are one sheet, not eight
    sportsSuperstar: [[3, 4, 5, 6, 7, 8, 9, 10]],
    // two 5x7 on one sheet; the eight wallets on another
    sportsAllStar: [[0, 1], [4, 5, 6, 7, 8, 9, 10, 11]],
    // two 5x7 on one sheet; each 2x2 wallet block is its own sheet
    sportsPicturePerfect: [[0, 1], [3, 4, 5, 6], [7, 8, 9, 10]],
    // two 5x7 stacked vertically in one frame; the eight wallets one sheet
    sportsBasic: [[1, 2], [3, 4, 5, 6, 7, 8, 9, 10]],
  };

  it.each(Object.entries(SHEETS))('%s keeps every sheet on a single pose', (key, sheets) => {
    sheets.forEach((indices) => {
      const poses = indices.map((i) => LAYOUTS[key][i].pose);
      expect(new Set(poses).size).toBe(1);
    });
  });

  it('still shows both poses on the card, across separate sheets', () => {
    // The point of the rule is not uniformity -- a customer should see both
    // poses they were photographed in, just never two on one sheet.
    ['sportsSuperstar', 'sportsAllStar', 'sportsPicturePerfect', 'sportsBasic'].forEach((key) => {
      const poses = new Set(LAYOUTS[key].filter(Boolean).map((slot) => slot.pose));
      expect(poses.has('pose1')).toBe(true);
      expect(poses.has('pose2')).toBe(true);
    });
  });
});

// ---------------------------------------------------------------------------
// Stack specs — see stackSlivers in AbsolutePosImgs for what they expand into.
describe('LAYOUTS — stacked sheets', () => {
  const stacked = (key) => LAYOUTS[key].filter(Boolean).filter((s) => s.stack);

  it('only the arts that actually draw a stack carry one', () => {
    // Measured by scanning each blank file for the white border of every sheet.
    // 8x10 + 5x7 + 3x5, plus the five wallets on the sheet's top row and left
    // column (r1c1..c4 and r2c1); the three interior wallets carry none.
    expect(stacked('sportsSuperstar')).toHaveLength(8);
    expect(stacked('sportsPicturePerfect')).toHaveLength(3); // small wallet block: r1c1, r1c2, r2c1
    expect(stacked('sportsAllStar')).toHaveLength(2); // the two 8x10 stacks
    // Basic's sheets are single, and so are the three one-slot options.
    ['sportsBasic', 'sports8x10', 'sportsTeamPicture', 'sportsAllDigital']
      .forEach((key) => expect(stacked(key)).toHaveLength(0));
  });

  it('describes every stack with a count of 2+ and a small positive pitch', () => {
    Object.keys(LAYOUTS).forEach((key) => {
      stacked(key).forEach(({ stack }) => {
        expect(stack.count).toBeGreaterThanOrEqual(2);
        // a sheet pitch is a few px on a 1200px canvas — a big number here
        // means someone put a canvas-height percentage in dy
        expect(stack.dx).toBeGreaterThan(0);
        expect(stack.dy).toBeGreaterThan(0);
        expect(stack.dx).toBeLessThan(3);
        expect(stack.dy).toBeLessThan(3);
      });
    });
  });

  // ⚠️ The sheet in front is opaque, so on a multi-print sheet only the top row
  // and the left column are ever visible behind it. An interior print with a
  // stack would paint its sliver over the white divider the artwork draws
  // between prints -- which is exactly the bug this encodes against.
  it('exposes only the top row and left column of a multi-print sheet', () => {
    // Superstar's eight wallets are slots 3..10: r1c1..c4 then r2c1..c4.
    const w = LAYOUTS.sportsSuperstar;
    expect(w[3].stack.edges).toBeUndefined(); // r1c1 — defaults to 'LT'
    [4, 5, 6].forEach((i) => expect(w[i].stack.edges).toBe('T'));
    expect(w[7].stack.edges).toBe('L'); // r2c1
    [8, 9, 10].forEach((i) => expect(w[i].stack).toBeUndefined());

    // Picture Perfect's small block is 2x2 at slots 3..6.
    const p = LAYOUTS.sportsPicturePerfect;
    expect(p[3].stack.edges).toBeUndefined();
    expect(p[4].stack.edges).toBe('T');
    expect(p[5].stack.edges).toBe('L');
    expect(p[6].stack).toBeUndefined();
  });

  it('keeps every print on a stacked sheet on the same pitch', () => {
    // A sheet is one piece of paper: its prints cannot slide apart.
    const pitches = [3, 4, 5, 6, 7].map((i) => {
      const { dx, dy } = LAYOUTS.sportsSuperstar[i].stack;
      return `${dx}/${dy}`;
    });
    expect(new Set(pitches).size).toBe(1);
  });
});
